import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { get, set } from "@totalsoft/change-tracking-react";
import { useHistory } from "react-router-dom";
import NegotiationCard from "./NegotiationCard";
import {
  INSURANCE_NEGOTIATION_REQUESTED,
  INSURANCE_NEGOTIATION_STARTED,
  REQUIRED_DOCUMENTS_EXISTENCE_CHECKED
} from "../../subscriptions";
import { useToast, useActivityIndicatorContext } from "utils/hooks";
import { useTranslation } from "react-i18next";
import { useSubscription } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { REQUEST_INSURANCE_NEGOTIATION, CHECK_REQUIRED_DOCUMENTS_EXISTENCE } from "../../mutations";
import { useServerErrorHandling } from "utils/hooks/useServerErrorHandling";
import { emptyArray, emptyString } from "constants/emptyConst";
import { useInsuranceTypeConfiguration } from "utils/hooks/useInsuranceTypeConfiguration";
import useInsuranceParams from "../../useInsuranceParams";

const NegotiationContainer = ({ simulation, lens, resetLens, isReadOnly }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const addToast = useToast();

  const insuranceTypeConfig = useInsuranceTypeConfiguration();
  const { offerId, offerSiteId, documentDetailId, documentDetailSiteId } = useInsuranceParams();

  const [saving, setSaving] = useState(false);

  const isReadOnlyInternally = useCallback(() => {
    if (
      saving ||
      (simulation.insuranceTypeCode === insuranceTypeConfig.rca?.propertyCode &&
        !simulation.selectedInsuranceQuotationId)
    ) {
      return true;
    }
    return false;
  }, [simulation.selectedInsuranceQuotationId, simulation.insuranceTypeCode, saving, insuranceTypeConfig]);

  const readOnly = useMemo(() => isReadOnly(isReadOnlyInternally), [isReadOnlyInternally, isReadOnly]);

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
      onEndCall()(lens.documentVerificationError |> set)(true);
    }
  });

  // #region Check Required Document

  const [checkRequiredDocumentsExistence] = useMutation(CHECK_REQUIRED_DOCUMENTS_EXISTENCE, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, "error");
      onEndCall();
    },
    onCompleted: data => {
      const { correlationId } = data.checkRequiredDocumentsExistence;
      storeKey({ correlationId });
    }
  });

  const handleNegotiationChecked = value => {
    if (value) {
      checkRequiredDocumentsExistence({
        variables: {
          offerId,
          offerSiteId,
          documentDetailId,
          documentDetailSiteId,
          insuranceSimulationId: simulation.id
        }
      });
      onBeginCall();
    }
    (lens.negotiationChecked |> set)(value);
  };

  // #endregion

  // #region Request Negotiation

  const [requestInsuranceNegotiation] = useMutation(REQUEST_INSURANCE_NEGOTIATION, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, "error");
      onEndCall();
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.requestInsuranceNegotiation;
      storeKey({ correlationId });
    }
  });

  const handleConfirmNegotiation = useCallback(() => {
    let negotiationQuotationsIds = [];
    const selectedInsuranceQuotationId = simulation.selectedInsuranceQuotationId;

    if (!selectedInsuranceQuotationId && simulation.insuranceTypeCode === insuranceTypeConfig.rca?.propertyCode) {
      addToast(t("Offer.Insurance.NegotiationOptionWarning"), "info");
      return;
    }

    if (
      simulation.insuranceTypeCode === insuranceTypeConfig.casco?.propertyCode &&
      (lens.selectedQuotations |> get).length > 0
    )
      negotiationQuotationsIds = (lens.selectedQuotations |> get).map(el => el.id);
    else if (selectedInsuranceQuotationId) negotiationQuotationsIds.push(selectedInsuranceQuotationId);

    requestInsuranceNegotiation({
      variables: {
        insuranceSimulationId: simulation.id,
        insuranceTypeCode: simulation.insuranceTypeCode,
        negotiationQuotationsIds,
        negotiationReason: lens.negotiationReason |> get
      }
    });
    setSaving(true);
    onBeginCall();
  }, [t, onBeginCall, simulation, lens, requestInsuranceNegotiation, insuranceTypeConfig, addToast]);

  // #endregion

  const handleOpenRequiredDocuments = useCallback(() => {
    history.push(`/offers/${offerId}/${offerSiteId}/documents`);
  }, [offerId, offerSiteId, history]);

  // #region Subscriptions

  useSubscription(REQUIRED_DOCUMENTS_EXISTENCE_CHECKED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({ subscriptionData }) => {
      if (!subscriptionData?.data?.requiredDocumentsExistenceChecked) return;

      const { mandatoryRequiredDocuments, optionalRequiredDocuments, clientId } =
        subscriptionData.data.requiredDocumentsExistenceChecked;

      const negotiation = lens |> get;
      resetLens({
        ...negotiation,
        clientId,
        mandatoryRequiredDocuments,
        optionalRequiredDocuments
      });
      onEndCall();
    }
  });

  useSubscription(INSURANCE_NEGOTIATION_REQUESTED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({ subscriptionData }) => {
      if (!subscriptionData?.data?.insuranceNegotiationRequested) return;

      const negotiation = lens |> get;
      resetLens({
        ...negotiation,
        negotiationChecked: false,
        documentVerificationError: false,
        negotiationReason: emptyString,
        selectedQuotations: emptyArray,
        negotiationInProgress: true
      });
      onEndCall();
      setSaving(false);
      addToast(t("Offer.Insurance.NegotiationRequested"), "info");
    }
  });

  useSubscription(INSURANCE_NEGOTIATION_STARTED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({ subscriptionData }) => {
      if (!subscriptionData?.data?.insuranceNegotiationStarted) return;

      if (subscriptionData.data.insuranceNegotiationStarted.message)
        addToast(subscriptionData.data.insuranceNegotiationStarted.message, "warning");
    }
  });

  // #endregion

  return (
    <NegotiationCard
      lens={lens}
      insuranceTypeCode={simulation.insuranceTypeCode}
      onNegotiationChecked={handleNegotiationChecked}
      onOpenRequiredDocuments={handleOpenRequiredDocuments}
      onConfirmNegotiation={handleConfirmNegotiation}
      readOnly={readOnly}
    />
  );
};

NegotiationContainer.propTypes = {
  simulation: PropTypes.object.isRequired,
  lens: PropTypes.object,
  resetLens: PropTypes.func,
  isReadOnly: PropTypes.func.isRequired,
  isImage: PropTypes.bool.isRequired
};

export default NegotiationContainer;
